import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconVideo } from "@tabler/icons-react";

import { Input, Text } from "@fragments";
import { VideoUploadField } from "@features/videoUpload";
import { AssetCategory, useUploadCommunityAsset } from "@features/uploadAsset";

import api from "@helpers/api";
import videoHelpers from "@helpers/video.js";

import "./styles.scss";

const THUMBNAIL_NAME = "welcome-video-thumbnail.jpg";
// Core accepts 255 / 5000; the home card clamps to two and three lines.
const NAME_MAX = 80;
const DESCRIPTION_MAX = 500;
const NAME_MIN = 2;

/**
 * Welcome-video setting: the upload-then-save piece wired to
 * `PUT | DELETE community/{id}/welcome-video`, plus the home card's title and
 * text. A new upload saves itself the moment it lands — the endpoint is
 * separate from PATCH community (creators may use it too) — and the title and
 * text ride along with it. Once a video is saved, editing them marks the tab
 * dirty and `controlRef.current.save()` sends them with the tab's Save button:
 * the same PUT with the same video, which core updates in place.
 *
 * Declared outside `General` on purpose: sections declared inside it remount
 * on every re-render, and a remount aborts the upload in flight.
 *
 * @param {number} communityId
 * @param {object|null} value     `welcome_video` from the community payload
 * @param {(next: object|null) => void} [onChange]  the saved value, or null once removed
 * @param {() => void} [onDirty]  the saved video's title or text changed
 * @param {{ current: { save: () => Promise<boolean> } | null }} [controlRef]
 */
export default function WelcomeVideo({ communityId, value, onChange, onDirty, controlRef }) {
    const { t } = useTranslation("settings");
    const { uploadFile } = useUploadCommunityAsset({ communityId });

    const [saved, setSaved] = useState(value ?? null);
    const [name, setName] = useState(value?.name ?? "");
    const [description, setDescription] = useState(value?.description ?? "");
    const [saving, setSaving] = useState(false);

    // Frame grabbed from the picked file, uploaded as the home card's thumbnail.
    const thumbnailRef = useRef(null);

    // Latest values for the imperative save, so `controlRef` binds once.
    const savedRef = useRef(saved);
    savedRef.current = saved;
    const textRef = useRef({ name, description });
    textRef.current = { name, description };

    useEffect(() => {
        setSaved(value ?? null);
        setName(value?.name ?? "");
        setDescription(value?.description ?? "");
    }, [value?.id]);

    useEffect(() => {
        if (!controlRef) return undefined;
        controlRef.current = { save: saveText };
        return () => {
            controlRef.current = null;
        };
    }, [controlRef]);

    function textPayload() {
        const current = textRef.current;
        const trimmedName = current.name.trim();
        const trimmedDescription = current.description.trim();
        return {
            name: trimmedName.length >= NAME_MIN ? trimmedName : null,
            description: trimmedDescription || null,
        };
    }

    async function put(video, banner) {
        const { data } = await api.put(`community/${communityId}/welcome-video`, {
            ...textPayload(),
            video,
            banner,
        });
        if (!data?.id) return null;

        setSaved(data);
        onChange?.(data);
        return data;
    }

    // Title/text of an already saved video: the same PUT with the same video.
    async function saveText() {
        const current = savedRef.current;
        if (!current?.id || !current?.src) return true;

        const next = textPayload();
        const unchanged = next.name === (current.name ?? null)
            && next.description === (current.description ?? null);
        if (unchanged) return true;

        try {
            const data = await put(
                {
                    src: current.src,
                    duration: current.duration ?? null,
                    aspect_ratio: current.aspect_ratio ?? null,
                },
                current.thumbnail ?? null,
            );
            if (data) return true;
        } catch (error) {
            console.log(error);
        }

        window.setSnackbar(t("general.welcomeVideo.saveError"));
        return false;
    }

    function handleNameInput(next) {
        setName(next);
        if (savedRef.current?.id) onDirty?.();
    }

    function handleDescriptionInput(next) {
        setDescription(next);
        if (savedRef.current?.id) onDirty?.();
    }

    async function handleFileSelected(localUrl) {
        thumbnailRef.current = null;
        try {
            thumbnailRef.current = await videoHelpers.generateThumbFromLocalVideo(localUrl);
        } catch (error) {
            console.log(error);
        }
    }

    // Best effort: a welcome video without a thumbnail still saves.
    async function uploadThumbnail() {
        const dataUrl = thumbnailRef.current;
        if (!dataUrl) return null;

        try {
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], THUMBNAIL_NAME, { type: "image/jpeg" });
            return await uploadFile(file, AssetCategory.CONTENT_BANNER);
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function handleChange(url, metadata) {
        if (!url) {
            await handleRemove();
            return;
        }

        setSaving(true);
        try {
            const banner = await uploadThumbnail();
            const data = await put(
                {
                    src: url,
                    duration: metadata?.duration ? Math.round(metadata.duration) : null,
                    aspect_ratio: metadata?.aspectRatio ?? null,
                },
                banner,
            );
            window.setSnackbar(t(data ? "general.welcomeVideo.saved" : "general.welcomeVideo.saveError"));
        } catch (error) {
            console.log(error);
            window.setSnackbar(t("general.welcomeVideo.saveError"));
        } finally {
            setSaving(false);
        }
    }

    async function handleRemove() {
        // Only a local pick was discarded: nothing saved to remove.
        if (!saved?.id) return;

        try {
            const response = await api.delete(`community/${communityId}/welcome-video`);
            if (response?.status === 204) {
                setSaved(null);
                onChange?.(null);
                window.setSnackbar(t("general.welcomeVideo.removed"));
            } else {
                window.setSnackbar(t("general.welcomeVideo.removeError"));
            }
        } catch (error) {
            console.log(error);
            window.setSnackbar(t("general.welcomeVideo.removeError"));
        }
    }

    const isPortrait = saved?.aspect_ratio && saved.aspect_ratio < 1;

    return (
        <div className="welcome-video-setting">
            <Text.h3
                value={t("general.welcomeVideo.title")}
                left={<IconVideo />}
                className="mt-4 mb-2"
            />

            <div className={`welcome-video-setting__field${isPortrait ? " is-portrait" : ""}`}>
                <VideoUploadField
                    value={saved?.src ?? null}
                    aspectRatio={isPortrait ? 9 / 16 : 16 / 9}
                    emptyTitle={t("general.welcomeVideo.fieldTitle")}
                    onChange={handleChange}
                    onFileSelected={handleFileSelected}
                />
            </div>

            {saving && (
                <Text.caption className="mt-1" loading>
                    {t("general.welcomeVideo.saving")}
                </Text.caption>
            )}

            <Input.Text
                variant="dark"
                className="mt-2"
                label={t("general.welcomeVideo.nameLabel")}
                placeholder={t("general.welcomeVideo.namePlaceholder")}
                size={NAME_MAX}
                value={name}
                onInput={handleNameInput}
                emoji
            />
            <Input.Text
                variant="dark"
                className="mt-2"
                label={t("general.welcomeVideo.descriptionLabel")}
                placeholder={t("general.welcomeVideo.descriptionPlaceholder")}
                size={DESCRIPTION_MAX}
                multiline
                value={description}
                onInput={handleDescriptionInput}
                emoji
            />
        </div>
    );
}
